import { IconType } from "react-icons";
import { FiAlertCircle, FiCheckCircle, FiXCircle } from "react-icons/fi";

import { cx } from "@/lib/tailwind-utils";

/**
 * Shared visual vocabulary for Spakinect GFE per-treatment outcomes.
 *
 * Spakinect adjudicates each requested treatment into one of three buckets once a
 * visit completes: approved, conditionally approved, or deferred (declined). These
 * are Spakinect's own classification — we render them 1:1, never color alone:
 * every outcome carries an icon + text label + status color token.
 *
 * Ordering everywhere is Deferred -> Conditional -> Approved (most-actionable first).
 */

interface GFEOutcomes {
  approvedTreatments?: string[] | null;
  conditionallyApprovedTreatments?: string[] | null;
  deferredTreatments?: string[] | null;
}

/** True when the GFE has any per-treatment outcome data (i.e. the visit completed). */
export function hasGFEOutcomes(gfe: GFEOutcomes): boolean {
  return Boolean(
    gfe.approvedTreatments?.length ||
    gfe.conditionallyApprovedTreatments?.length ||
    gfe.deferredTreatments?.length,
  );
}

/** True when Spakinect declined (deferred) at least one treatment. */
export function hasDeferredTreatments(gfe: GFEOutcomes): boolean {
  return Boolean(gfe.deferredTreatments?.length);
}

interface GFEOutcomeGroupsProps extends GFEOutcomes {
  /**
   * Collapse the approved bucket to a count (names hidden) to save space — the
   * approved list is the calm happy-path; deferred/conditional stay fully listed.
   */
  collapseApproved?: boolean;
  className?: string;
}

/** Grouped outcome rows: Deferred / Conditional / Approved, each icon + label + names. */
export function GFEOutcomeGroups({
  approvedTreatments,
  conditionallyApprovedTreatments,
  deferredTreatments,
  collapseApproved = false,
  className,
}: GFEOutcomeGroupsProps) {
  const deferred = deferredTreatments ?? [];
  const conditional = conditionallyApprovedTreatments ?? [];
  const approved = approvedTreatments ?? [];

  if (!deferred.length && !conditional.length && !approved.length) {
    return null;
  }

  return (
    <div className={cx("flex flex-col gap-1", className)}>
      {deferred.length > 0 && (
        <OutcomeRow
          icon={FiXCircle}
          colorClass="text-status-error-text"
          label="Deferred"
          treatments={deferred}
        />
      )}
      {conditional.length > 0 && (
        <OutcomeRow
          icon={FiAlertCircle}
          colorClass="text-status-orange-text"
          label="Conditional"
          treatments={conditional}
        />
      )}
      {approved.length > 0 && (
        <OutcomeRow
          icon={FiCheckCircle}
          colorClass="text-status-success-text"
          label="Approved"
          treatments={approved}
          showNames={!collapseApproved}
        />
      )}
    </div>
  );
}

function OutcomeRow({
  icon: Icon,
  colorClass,
  label,
  treatments,
  showNames = true,
}: {
  icon: IconType;
  colorClass: string;
  label: string;
  treatments: string[];
  showNames?: boolean;
}) {
  return (
    <div className="flex items-start gap-1.5 text-caption leading-tight">
      <Icon
        className={cx("mt-px h-3.5 w-3.5 shrink-0", colorClass)}
        aria-hidden
      />
      <span className={cx("shrink-0 font-medium", colorClass)}>
        {showNames ? label : `${label} (${treatments.length})`}
      </span>
      {showNames && (
        <span className="min-w-0 text-foreground-secondary">
          {treatments.join(", ")}
        </span>
      )}
    </div>
  );
}

/**
 * Compact icon + count pills (Deferred / Conditional / Approved) for space-constrained
 * surfaces such as the checklist sidebar. Only nonzero buckets render; deferred first.
 * Each pill is labelled for screen readers since the count alone is icon-only.
 */
export function GFEOutcomeMiniBar({
  approvedTreatments,
  conditionallyApprovedTreatments,
  deferredTreatments,
  className,
}: GFEOutcomeGroupsProps) {
  const pills = [
    {
      count: deferredTreatments?.length ?? 0,
      icon: FiXCircle,
      colorClass: "text-status-error-text",
      noun: "deferred",
    },
    {
      count: conditionallyApprovedTreatments?.length ?? 0,
      icon: FiAlertCircle,
      colorClass: "text-status-orange-text",
      noun: "conditional",
    },
    {
      count: approvedTreatments?.length ?? 0,
      icon: FiCheckCircle,
      colorClass: "text-status-success-text",
      noun: "approved",
    },
  ].filter((pill) => pill.count > 0);

  if (!pills.length) {
    return null;
  }

  return (
    <div className={cx("flex items-center gap-2", className)}>
      {pills.map(({ count, icon: Icon, colorClass, noun }) => (
        <span
          key={noun}
          className={cx(
            "flex items-center gap-0.5 text-tiny font-medium",
            colorClass,
          )}
          aria-label={`${count} ${noun} treatment${count === 1 ? "" : "s"}`}
        >
          <Icon className="h-3 w-3 shrink-0" aria-hidden />
          {count}
        </span>
      ))}
    </div>
  );
}
